import { useEffect, useRef, useState, type ReactNode } from 'react';
import { defaultPieces } from 'react-chessboard';
import type { Color, Look } from '../../shared/protocol.ts';
import { PlayerAvatar, PlayerName } from './PlayerLook.tsx';
import { formatClock, type PieceCounts } from '../lib/chess.ts';
import { sound } from '../lib/sound.ts';

interface PlayerBarProps {
  name: string;
  avatar: ReactNode;
  color: Color;
  /** Pieces this player has captured. */
  captured: PieceCounts;
  lead: number;
  clock?: { ms: number; running: boolean; anchorAt: number } | null;
  status?: ReactNode;
  /** Play a warning tick when the user's own clock runs low. */
  isSelf?: boolean;
  /** Shown next to the name in rated games. */
  rating?: number | null;
  /** Online players: their avatar, frame and name style (null for guests). */
  look?: Look | null;
}

const ORDER = ['p', 'n', 'b', 'r', 'q'] as const;

export function PlayerBar({ name, avatar, color, captured, lead, clock, status, isSelf, rating, look }: PlayerBarProps) {
  const victim = color === 'w' ? 'b' : 'w';
  return (
    <div className="player-bar">
      {look !== undefined ? (
        <PlayerAvatar look={look} name={name} size="md" />
      ) : (
        <div className="avatar" aria-hidden="true">
          {avatar}
        </div>
      )}
      <div className="player-meta">
        <div className="player-name">
          {look !== undefined ? <PlayerName name={name} look={look} /> : <span className="player-name-text">{name}</span>}
          {rating != null && <span className="badge badge-accent rating-chip">🏆 {rating}</span>}
          {status}
        </div>
        <div className="player-sub">
          <span className="captured" aria-hidden="true">
            {ORDER.map((p) =>
              captured[p] > 0 ? (
                <span key={p} className="captured-group">
                  {Array.from({ length: captured[p] }, (_, i) => (
                    <span key={i} className="captured-piece">
                      {defaultPieces[`${victim}${p.toUpperCase()}`]?.({ svgStyle: { width: '100%', height: '100%' } })}                    </span>
                  ))}
                </span>
              ) : null,
            )}
          </span>
          {lead > 0 && <span className="material-diff">+{lead}</span>}
        </div>
      </div>
      {clock && <Clock ms={clock.ms} running={clock.running} anchorAt={clock.anchorAt} warn={isSelf} />}
    </div>
  );
}

const LOW_TIME_MS = 20_000;

/**
 * Counts down locally between server updates. `anchorAt` is the client time
 * (Date.now) at which `ms` was accurate.
 */
function Clock({ ms, running, anchorAt, warn }: { ms: number; running: boolean; anchorAt: number; warn?: boolean }) {
  const [now, setNow] = useState(Date.now);
  const lastRemaining = useRef(ms);

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      const current = Date.now();
      const remaining = ms - Math.max(0, current - anchorAt);
      if (warn && lastRemaining.current >= LOW_TIME_MS && remaining < LOW_TIME_MS) sound.play('lowTime');
      lastRemaining.current = remaining;
      setNow(current);
    }, 100);
    return () => window.clearInterval(id);
  }, [ms, running, anchorAt, warn]);

  const remaining = running ? ms - Math.max(0, now - anchorAt) : ms;
  const low = remaining < LOW_TIME_MS;

  return (
    <div className={`clock${running ? ' active' : ''}${running && low ? ' low' : ''}`} role="timer" aria-live="off">
      {formatClock(remaining)}
    </div>
  );
}

/** Shown on the player's own bar so the cue sits right next to the board, even on phones. */
export function YourTurnBadge({ label, check }: { label: string; check?: boolean }) {
  return <span className={`badge turn-pill ${check ? 'badge-danger' : 'badge-green'}`}>{label}</span>;
}

export function ThinkingDots({ label }: { label: string }) {
  return (
    <span className="badge badge-accent" role="status">
      <span className="thinking" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label}
    </span>
  );
}
